import classNames from "classnames";
import { useCallback, useState, Fragment } from "react";
import { ChromePicker } from "react-color";
import { Colors } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import useUpdateNode from "../../hooks/useUpdateNode";
import { HexInput } from "../HexInput";
import { RadioButtonWithTooltip } from "../RadioButtonWithTooltip";
import { ButtonOrLinkWithTooltip } from "../ButtonOrLinkWithTooltip";
import styles from "./ColorPicker.module.css";

type Props = {
  id: string;
  color: string;
  showHexInput: boolean;
  colorOptions?: ColorOption[];
  onClose?: () => void;
};

type CustomButtonProps = {
  color: string;
  handleColorChange: (color: string) => void;
};

export type ColorOption = {
  label: string;
  color: string;
};

export const COLOR_PICKER_OPTIONS = [
  { label: "White", color: Colors.BackgroundDefault },
  { label: "Black", color: "#252525" },
  { label: "Yellow", color: "#F3D05E" },
  { label: "Orange", color: "#F78851" },
  { label: "Red", color: "#F35454" },
  { label: "Green", color: "#5CDD7E" },
  { label: "Blue", color: "#6BB7FA" },
  { label: "Gray", color: Colors.GrayWarm4 },
  { label: "Peach", color: Colors.BrandPeach },
  { label: "Light Purple", color: Colors.BrandLavender },
  { label: "Dark Indigo", color: Colors.BrandNight },
  { label: "Light Aqua", color: Colors.BrandSky },
  { label: "Dark Gray", color: Colors.GrayWarm9 },
];

// Colors that need a white border to be visible against dark background
const COLORS_NEEDING_BORDER = ["#252525", "#413735"];

function needsWhiteBorder(color: string): boolean {
  return COLORS_NEEDING_BORDER.includes(color);
}

function CustomColorPicker({ color, handleColorChange }: CustomButtonProps) {
  return (
    <ChromePicker
      color={color}
      onChange={(color, event) => {
        event.stopPropagation();
        handleColorChange(color.hex);
      }}
      disableAlpha
    />
  );
}

export function ColorPicker({
  id,
  color,
  showHexInput,
  colorOptions = COLOR_PICKER_OPTIONS,
  onClose,
}: Props) {
  const updateNodeAndSave = useUpdateNode();
  const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
  const colorGroup1 = colorOptions.slice(0, 7); // Items 1–7
  const colorGroup2 = colorOptions.slice(7, colorOptions.length); // Items 8–13

  const isPredefinedColor = colorOptions.some(
    (option) => option.color === color,
  );

  const handleColorChange = useCallback(
    (color: string) => {
      updateNodeAndSave({
        id,
        data: {
          color,
        },
      });
    },
    [updateNodeAndSave, id],
  );

  const openCustomColorPicker = useCallback(
    () => setIsCustomPickerOpen(true),
    [setIsCustomPickerOpen],
  );

  if (isCustomPickerOpen) {
    return (
      <CustomColorPicker color={color} handleColorChange={handleColorChange} />
    );
  }

  return (
    <div className={classNames(styles.colorPicker)}>
      <div className={styles.colorPickerGroupings}>
        <div className={styles.colorGroup}>
          {colorGroup1.map((option) => {
            const isChecked = color === option.color;

            return (
              <Fragment key={option.color}>
                <RadioButtonWithTooltip
                  content={option.label}
                  className={styles.colorOption}
                  name="color"
                  value={option.color}
                  isChecked={isChecked}
                  onChange={handleColorChange}
                  ariaLabel={`Select color ${option.color}`}
                >
                  <div
                    className={classNames(styles.colorDotOuterBorder, {
                      [styles.colorDotOuterBorderChecked]: isChecked,
                    })}
                  >
                    <div
                      className={classNames(styles.colorDotInnerBorder, {
                        [styles.colorDotInnerBorderChecked]: isChecked,
                      })}
                    >
                      <span
                        className={classNames(styles.colorDot, {
                          [styles.colorDotChecked]: isChecked,
                          [styles.colorDotWithLightBorder]:
                            needsWhiteBorder(option.color) && !isChecked,
                        })}
                        style={{ backgroundColor: option.color }}
                      />
                    </div>
                  </div>
                </RadioButtonWithTooltip>
              </Fragment>
            );
          })}
        </div>
        <div className={styles.colorGroup}>
          {colorGroup2.map((option) => {
            const isChecked = color === option.color;

            return (
              <Fragment key={option.color}>
                <RadioButtonWithTooltip
                  content={option.label}
                  className={styles.colorOption}
                  name="color"
                  value={option.color}
                  isChecked={isChecked}
                  onChange={handleColorChange}
                  ariaLabel={`Select color ${option.color}`}
                >
                  <div
                    className={classNames(styles.colorDotOuterBorder, {
                      [styles.colorDotOuterBorderChecked]: isChecked,
                    })}
                  >
                    <div
                      className={classNames(styles.colorDotInnerBorder, {
                        [styles.colorDotInnerBorderChecked]: isChecked,
                      })}
                    >
                      <span
                        className={classNames(styles.colorDot, {
                          [styles.colorDotChecked]: isChecked,
                          [styles.colorDotWithLightBorder]:
                            needsWhiteBorder(option.color) && !isChecked,
                        })}
                        style={{ backgroundColor: option.color }}
                      />
                    </div>
                  </div>
                </RadioButtonWithTooltip>
              </Fragment>
            );
          })}
        </div>
        <div className={styles.colorGroup}>
          <RadioButtonWithTooltip
            content="Custom"
            className={styles.colorOption}
            name="color"
            value="custom"
            onChange={openCustomColorPicker}
            ariaLabel="Select custom color"
          >
            <div
              className={classNames(styles.colorDotOuterBorder, {
                [styles.colorDotOuterBorderChecked]: !isPredefinedColor,
              })}
            >
              <div
                className={classNames(styles.colorDotInnerBorder, {
                  [styles.colorDotInnerBorderChecked]: !isPredefinedColor,
                })}
              >
                <span
                  className={classNames(styles.colorDot, styles.customCircle, {
                    [styles.colorDotChecked]: !isPredefinedColor,
                  })}
                />
              </div>
            </div>
          </RadioButtonWithTooltip>
          {showHexInput && (
            <div className={styles.hexInputWrapper}>
              <span className={styles.hashTag}>#</span>
              <HexInput id={id} color={color}>
                {(inputProps) => (
                  <input
                    type="text"
                    id="hexColor"
                    name="hexColor"
                    className={styles.hexInput}
                    {...inputProps}
                  />
                )}
              </HexInput>
            </div>
          )}
          {onClose && (
            <ButtonOrLinkWithTooltip
              content="Close menu"
              className={styles.closeButton}
              onClick={onClose}
            >
              <Icon canAcceptPointerEvents={false} name="formX" size={20} />
            </ButtonOrLinkWithTooltip>
          )}
        </div>
      </div>
    </div>
  );
}
